import React, { useState, useRef } from 'react';
import { FiUpload, FiX, FiFile, FiExternalLink } from 'react-icons/fi';
import { FaFilePdf, FaFileAlt } from 'react-icons/fa';

interface DragDropFileInputProps {
  label: string;
  accept?: string;
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  existingFile?: string | null;
}

function isImageUrl(url: string): boolean {
  const lower = url.toLowerCase().split('?')[0];
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(lower);
}

function isPdfUrl(url: string): boolean {
  return url.toLowerCase().split('?')[0].endsWith('.pdf');
}

const DragDropFileInput: React.FC<DragDropFileInputProps> = ({
  label,
  accept = 'image/*,.pdf',
  onFileSelect,
  selectedFile,
  existingFile,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [newFilePreview, setNewFilePreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      generatePreview(file);
      onFileSelect(file);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const generatePreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setNewFilePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setNewFilePreview(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) generatePreview(file);
    else setNewFilePreview(null);
    onFileSelect(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="border border-gray-200 rounded p-3">
      <label className="block text-xs font-medium text-gray-700 mb-2">{label}</label>

      {/* Existing file preview (shown when no new file selected) */}
      {!selectedFile && existingFile && (
        <div className="mb-2">
          {isImageUrl(existingFile) ? (
            <div className="relative group">
              <img
                src={existingFile}
                alt={label}
                className="w-full h-24 object-contain rounded border border-gray-200 bg-gray-50"
              />
              <a
                href={existingFile}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded"
              >
                <FiExternalLink className="w-5 h-5 text-white" />
              </a>
            </div>
          ) : isPdfUrl(existingFile) ? (
            <a
              href={existingFile}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 hover:bg-red-100"
            >
              <FaFilePdf className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">View PDF</span>
              <FiExternalLink className="w-3 h-3 flex-shrink-0 ml-auto" />
            </a>
          ) : (
            <a
              href={existingFile}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 hover:bg-gray-100"
            >
              <FaFileAlt className="w-4 h-4 flex-shrink-0 text-gray-500" />
              <span className="truncate">View File</span>
              <FiExternalLink className="w-3 h-3 flex-shrink-0 ml-auto" />
            </a>
          )}
        </div>
      )}

      {/* New file preview */}
      {selectedFile && newFilePreview && (
        <div className="mb-2">
          <img
            src={newFilePreview}
            alt="Preview"
            className="w-full h-24 object-contain rounded border border-green-200 bg-green-50"
          />
        </div>
      )}

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`relative border-2 border-dashed rounded p-3 text-center cursor-pointer transition-all duration-200 ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : selectedFile
            ? 'border-green-300 bg-green-50'
            : existingFile
            ? 'border-gray-300 bg-gray-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          onChange={handleChange}
          accept={accept}
          className="hidden"
        />

        {selectedFile ? (
          <div className="flex items-center justify-center gap-2">
            <FiFile className="w-4 h-4 text-green-600" />
            <span className="text-xs text-green-700 font-medium truncate max-w-[180px]">
              {selectedFile.name}
            </span>
          </div>
        ) : existingFile ? (
          <span className="text-xs text-gray-400">Drop new file to replace</span>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <FiUpload className="w-5 h-5 text-gray-400" />
            <span className="text-xs text-gray-500">Drag & drop or click</span>
          </div>
        )}
      </div>

      {selectedFile && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setNewFilePreview(null);
            onFileSelect(null);
          }}
          className="mt-1.5 flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
        >
          <FiX className="w-3 h-3" />
          Remove
        </button>
      )}
    </div>
  );
};

export default DragDropFileInput;
